import { Box, type SxProps } from "@mui/material";
import { DiscreteScaleSlider, DiscreteScaleStars } from "../../components/ui";
import ActionButton from "../../components/ui/ActionButton";
import { questionCardSurface } from "../../components/ui/QuestionSurface/styles";
import { useThemeContext } from "../../globalContext/global-context";
import type { ThemeV2 } from "../../util/types";
import type { QuestionViewProps } from "../contracts";

import type { RatingDefinition, RatingDraft, RatingEvent } from "./core";
import { ratingButtonDescription } from "./labels";
import {
  buttonValueContainer,
  buttonValueContent,
  ratingParentContainer,
  sliderMarkValues,
  textMark,
} from "./styles";

type RatingViewProps = QuestionViewProps<
  RatingDefinition,
  RatingDraft,
  RatingEvent
>;

const fullyLabelled = (definition: RatingDefinition) =>
  definition.options.every(
    (option) => option.displayLabel && option.label.trim(),
  );

const RatingLabels = ({
  definition,
  theme,
  view,
}: {
  definition: RatingDefinition;
  theme: ThemeV2;
  view: string;
}) => (
  <Box
    data-rating-labels
    sx={{
      display: "grid",
      gridTemplateColumns: `repeat(${definition.options.length}, minmax(0, 1fr))`,
      width: "100%",
      mt: 1,
    }}
  >
    {definition.options.map((option, index) => (
      <Box
        key={option.value}
        sx={
          [
            textMark(theme),
            {
              justifySelf:
                view === "star"
                  ? "center"
                  : index === 0
                    ? "start"
                    : index === definition.options.length - 1
                      ? "end"
                      : "center",
              maxWidth: { xs: 90, sm: 150 },
              textAlign:
                view === "star"
                  ? "center"
                  : index === 0
                    ? "left"
                    : index === definition.options.length - 1
                      ? "right"
                      : "center",
              width: "max-content",
            },
          ] as SxProps
        }
      >
        {view === "buttons"
          ? ratingButtonDescription(option)
          : option.displayLabel
            ? option.label
            : ""}
      </Box>
    ))}
  </Box>
);

const RatingScaleContent = ({
  definition,
  theme,
  children,
  view,
}: {
  definition: RatingDefinition;
  theme: ThemeV2;
  children: React.ReactNode;
  view: string;
}) => (
  <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
    <Box sx={{ width: "100%", minWidth: 0 }}>
      {children}
      {(view !== "buttons" || !fullyLabelled(definition)) && (
        <RatingLabels definition={definition} theme={theme} view={view} />
      )}
    </Box>
  </Box>
);

const RatingDontKnow = ({ props }: { props: RatingViewProps }) => {
  const option = props.definition.dontKnow;
  if (!option) return null;
  return (
    <ActionButton
      currentVal={{ text: option.label, display_text: true }}
      isSelected={props.draft.selectedValue === option.value}
      disabled={!props.controls.canSubmit}
      onClick={() => props.submit({ type: "dont-know" })}
      testId="rating-dont-know"
    />
  );
};

const RatingFrame = ({
  props,
  children,
  card = false,
}: {
  props: RatingViewProps;
  children: React.ReactNode;
  card?: boolean;
}) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <Box
      data-testid={card ? "rating-card" : undefined}
      sx={
        [
          ratingParentContainer,
          ...(card
            ? [
                {
                  minWidth: 0,
                  padding: {
                    xs: `${theme.tokens.spacing["3"]}px`,
                    sm: `${theme.tokens.spacing["6"]}px`,
                  },
                  ...questionCardSurface(theme),
                },
              ]
            : []),
        ] as SxProps
      }
    >
      <RatingScaleContent
        definition={props.definition}
        theme={theme}
        view={props.display.view}
      >
        {children}
      </RatingScaleContent>
      <RatingDontKnow props={props} />
    </Box>
  );
};

export const RatingButtonsView = (props: RatingViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const labelsInButtons = fullyLabelled(props.definition);
  return (
    <RatingFrame props={props}>
      <Box sx={buttonValueContainer}>
        {props.definition.options.map((option, index) => (
          <ActionButton
            key={option.value}
            testId={`item-${index + 1}`}
            ariaLabel={`${option.value}${ratingButtonDescription(option) ? ` ${option.label}` : ""}`}
            ariaPressed={props.draft.selectedValue === option.value}
            onClick={() =>
              props.dispatch({ type: "select", value: option.value })
            }
            style={buttonValueContent(
              theme,
              props.draft.selectedValue === option.value,
            )}
          >
            {labelsInButtons ? option.label : option.value}
          </ActionButton>
        ))}
      </Box>
    </RatingFrame>
  );
};

export const RatingStarsView = (props: RatingViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <RatingFrame props={props} card>
      <DiscreteScaleStars
        testId="rating-stars"
        name="rating-stars"
        ariaLabel={theme.copy.labels.rating}
        points={props.definition.options}
        selectedValue={props.draft.selectedValue}
        onSelect={(value) => props.dispatch({ type: "select", value })}
        spread
      />
    </RatingFrame>
  );
};

export const RatingSliderView = (props: RatingViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  return (
    <RatingFrame props={props} card>
      <DiscreteScaleSlider
        testId="slider-rating"
        ariaLabel={theme.copy.labels.ratingScale}
        points={props.definition.options}
        selectedValue={props.draft.selectedValue}
        onSelect={(value) => props.dispatch({ type: "select", value })}
        showMarkValues
        sx={sliderMarkValues(theme)}
      />
    </RatingFrame>
  );
};
